import {
    cloneElement,
    type ForwardedRef,
    forwardRef,
    isValidElement,
    memo,
    type ReactElement,
    useImperativeHandle,
    useRef,
} from 'react';
import cn from 'classnames';
import { mergeRefs } from '@direct-frontend/stdlib';
import { useCarousel, type UseCarouselController } from './lib/hooks/useCarousel';
import { Iterator } from '../Iterator';
import styles from './Carousel.module.css';

export type ControlsAlignment = 'none' | 'edges' | 'leftRight' | 'left' | 'right' | 'bottomCenter' | 'bottomRight';

export interface CarouselSettings {
    /**
     * @title Количество карточек
     * @minimum 1
     */
    count: number;
}

export type CarouselProps = CarouselSettings & {
    enableScrollSnap?: boolean;
    className?: string;
    trackClassName?: string;
    currentIndex: number;
    onIndexChange?: (index: number) => void;
    renderItem: (index: number) => ReactElement<HTMLElement> | null;
    indicatorsSlot?: ReactElement | null;
    hideControls?: boolean;
    controlsAlignment?: ControlsAlignment;
};

export const Carousel = memo(
    forwardRef(function Carousel(props: CarouselProps, forwardedRef: ForwardedRef<UseCarouselController>) {
        const { className, trackClassName, count, renderItem, indicatorsSlot } = props;

        const carouselRef = useRef<HTMLDivElement | null>(null);
        const carouselTrackRef = useRef<HTMLDivElement | null>(null);
        const carouselController = useCarousel({ ...props, carouselRef, carouselTrackRef, vertical: true });

        useImperativeHandle(forwardedRef, () => carouselController, [carouselController]);

        return (
            <div ref={mergeRefs(carouselRef, forwardedRef as never)} className={cn(styles.root, className)}>
                <Iterator
                    ref={carouselTrackRef}
                    count={count}
                    className={cn(trackClassName, styles.track_vertical)}
                    onScroll={carouselController.onTrackScroll}
                    data-carousel-track
                    renderItem={(index: number) => {
                        const element = renderItem(index);

                        return isValidElement(element)
                            ? cloneElement(element, {
                                  key: index,
                                  className: cn(element.props.className, styles.slide),
                              })
                            : null;
                    }}
                />
                {indicatorsSlot}
            </div>
        );
    })
);
